import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Building2, User, Users, Briefcase } from 'lucide-react';

/**
 * OnboardingPage - Complete user profile after Google OAuth
 */
export default function OnboardingPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const user = location.state?.user || JSON.parse(localStorage.getItem('user') || '{}');
    
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        role: '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: '',
        // Agency fields (for admin)
        agency_name: '',
        agency_siret: '',
        agency_address: '',
        agency_city: '',
        agency_postal_code: '',
        agency_email: user.email || '',
        agency_phone: '',
        // Agent fields
        invite_code: ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleRoleSelect = (role) => {
        setFormData(prev => ({ ...prev, role }));
        setStep(2);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await api.post('/auth/onboarding', formData, { 
                withCredentials: true 
            });
            
            const { user: updatedUser } = response.data;
            localStorage.setItem('user', JSON.stringify(updatedUser));
            
            toast.success('Profil complété avec succès !');
            
            // Redirect to dashboard
            const dashboardRoutes = {
                admin: '/admin',
                agent: '/agent',
                seller: '/seller'
            };
            navigate(dashboardRoutes[updatedUser.role] || '/dashboard', { replace: true });
        } catch (error) {
            console.error('Onboarding error:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la création du profil');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#681793]/5 to-[#FF914D]/5 flex items-center justify-center p-4">
            <Card className="w-full max-w-lg">
                <CardHeader className="text-center">
                    <div className="mx-auto mb-4 w-16 h-16 bg-gradient-to-br from-[#681793] to-[#FF914D] rounded-2xl flex items-center justify-center">
                        <span className="text-white text-2xl font-bold">H</span>
                    </div>
                    <CardTitle className="text-2xl">Bienvenue sur Homly</CardTitle>
                    <CardDescription>
                        {step === 1 
                            ? 'Sélectionnez votre rôle pour continuer'
                            : 'Complétez votre profil'
                        }
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {step === 1 ? (
                        <div className="space-y-4">
                            <button
                                data-testid="role-admin-btn"
                                onClick={() => handleRoleSelect('admin')}
                                className="w-full p-6 border-2 rounded-xl hover:border-[#681793] hover:bg-[#681793]/5 transition-all flex items-center gap-4 text-left"
                            >
                                <div className="w-12 h-12 rounded-lg bg-[#681793]/10 flex items-center justify-center">
                                    <Building2 className="w-6 h-6 text-[#681793]" />
                                </div>
                                <div>
                                    <h3 className="font-semibold">Administrateur d'agence</h3>
                                    <p className="text-sm text-muted-foreground">
                                        Je souhaite créer mon agence immobilière
                                    </p>
                                </div>
                            </button>

                            <button
                                data-testid="role-agent-btn"
                                onClick={() => handleRoleSelect('agent')}
                                className="w-full p-6 border-2 rounded-xl hover:border-[#FF914D] hover:bg-[#FF914D]/5 transition-all flex items-center gap-4 text-left"
                            >
                                <div className="w-12 h-12 rounded-lg bg-[#FF914D]/10 flex items-center justify-center">
                                    <Briefcase className="w-6 h-6 text-[#FF914D]" />
                                </div>
                                <div>
                                    <h3 className="font-semibold">Agent commercial</h3>
                                    <p className="text-sm text-muted-foreground">
                                        J'ai un code d'invitation de mon agence
                                    </p>
                                </div>
                            </button>

                            <button
                                data-testid="role-seller-btn"
                                onClick={() => handleRoleSelect('seller')}
                                className="w-full p-6 border-2 rounded-xl hover:border-[#9C70C9] hover:bg-[#9C70C9]/5 transition-all flex items-center gap-4 text-left"
                            >
                                <div className="w-12 h-12 rounded-lg bg-[#9C70C9]/10 flex items-center justify-center">
                                    <User className="w-6 h-6 text-[#9C70C9]" />
                                </div>
                                <div>
                                    <h3 className="font-semibold">Vendeur</h3>
                                    <p className="text-sm text-muted-foreground">
                                        Je vends un bien avec une agence partenaire
                                    </p>
                                </div>
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Common fields */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="first_name">Prénom</Label>
                                    <Input
                                        id="first_name"
                                        name="first_name"
                                        data-testid="first-name-input"
                                        value={formData.first_name}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="last_name">Nom</Label>
                                    <Input
                                        id="last_name"
                                        name="last_name"
                                        data-testid="last-name-input"
                                        value={formData.last_name}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="phone">Téléphone</Label>
                                <Input
                                    id="phone"
                                    name="phone"
                                    data-testid="phone-input"
                                    type="tel"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    placeholder="+33 6 12 34 56 78"
                                />
                            </div>

                            {/* Admin-specific fields */}
                            {formData.role === 'admin' && (
                                <div className="space-y-4 pt-4 border-t">
                                    <h3 className="font-semibold">Informations de l'agence</h3>
                                    
                                    <div className="space-y-2">
                                        <Label htmlFor="agency_name">Nom de l'agence *</Label>
                                        <Input
                                            id="agency_name"
                                            name="agency_name"
                                            data-testid="agency-name-input"
                                            value={formData.agency_name}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="agency_siret">SIRET</Label>
                                        <Input
                                            id="agency_siret"
                                            name="agency_siret"
                                            data-testid="agency-siret-input"
                                            value={formData.agency_siret}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="agency_address">Adresse</Label>
                                        <Input
                                            id="agency_address"
                                            name="agency_address"
                                            data-testid="agency-address-input"
                                            value={formData.agency_address}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="agency_city">Ville</Label>
                                            <Input
                                                id="agency_city"
                                                name="agency_city"
                                                data-testid="agency-city-input"
                                                value={formData.agency_city}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="agency_postal_code">Code postal</Label>
                                            <Input
                                                id="agency_postal_code"
                                                name="agency_postal_code"
                                                data-testid="agency-postal-input"
                                                value={formData.agency_postal_code}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Agent-specific fields */}
                            {formData.role === 'agent' && (
                                <div className="space-y-4 pt-4 border-t">
                                    <h3 className="font-semibold">Rejoindre une agence</h3>
                                    
                                    <div className="space-y-2">
                                        <Label htmlFor="invite_code">Code d'invitation *</Label>
                                        <Input
                                            id="invite_code"
                                            name="invite_code"
                                            data-testid="invite-code-input"
                                            value={formData.invite_code}
                                            onChange={handleChange}
                                            required
                                            placeholder="Collez le code reçu par email"
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            Demandez à votre administrateur d'agence de vous envoyer une invitation
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Seller info */}
                            {formData.role === 'seller' && (
                                <div className="p-4 bg-muted rounded-lg">
                                    <p className="text-sm text-muted-foreground">
                                        En tant que vendeur, votre compte sera automatiquement lié 
                                        aux mandats créés avec votre adresse email ({user.email}).
                                    </p>
                                </div>
                            )}

                            <div className="flex gap-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setStep(1)}
                                    className="flex-1"
                                >
                                    Retour
                                </Button>
                                <Button
                                    type="submit"
                                    data-testid="complete-onboarding-btn"
                                    className="flex-1 bg-gradient-to-r from-[#681793] to-[#FF914D] text-white"
                                    disabled={loading}
                                >
                                    {loading ? 'Création...' : 'Terminer'}
                                </Button>
                            </div>
                        </form>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
